import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { Modal } from '@carbon/react';
import { useTheme } from '../context/ThemeContext';

const QRCodeScanner = ({ isOpen, onClose, onScanSuccess }) => {
  const { theme } = useTheme();
  const scannerContainerRef = useRef(null);
  const html5QrCodeRef = useRef(null);
  const isMountedRef = useRef(true);
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState('');

  const isDark = theme === 'dark';
  const bgSecondary = isDark ? '#262626' : '#f4f4f4';
  const textPrimary = isDark ? '#f4f4f4' : '#161616';

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    let isActive = true;

    const startScanning = async () => {
      if (!isOpen || !scannerContainerRef.current || html5QrCodeRef.current) {
        return;
      }

      try {
        setError('');
        setIsScanning(true);

        // Create a dedicated div for the scanner
        const scannerId = `qr-scanner-${Date.now()}`;
        const scannerDiv = document.createElement('div');
        scannerDiv.id = scannerId;
        scannerDiv.style.width = '100%';
        scannerDiv.style.minHeight = '400px';

        // Clear container and append scanner div safely
        if (scannerContainerRef.current && scannerContainerRef.current.parentNode) {
          try {
            // Safely clear existing children
            while (scannerContainerRef.current.firstChild) {
              scannerContainerRef.current.removeChild(scannerContainerRef.current.firstChild);
            }
            scannerContainerRef.current.appendChild(scannerDiv);
          } catch (err) {
            console.warn('Container setup warning:', err);
            return;
          }
        } else {
          console.warn('Scanner container not available');
          return;
        }

        const html5QrCode = new Html5Qrcode(scannerId);
        html5QrCodeRef.current = html5QrCode;

        await html5QrCode.start(
          { facingMode: 'environment' },
          {
            fps: 10,
            qrbox: { width: 250, height: 250 },
            aspectRatio: 1.0,
          },
          async (decodedText) => {
            if (!isActive || !isMountedRef.current) return;

            console.log('QR Code scanned:', decodedText);

            // Stop scanner before calling callback
            try {
              const scanner = html5QrCodeRef.current;
              if (scanner) {
                await scanner.stop();
                html5QrCodeRef.current = null;

                // Clear the scanner element safely
                try {
                  await scanner.clear();
                } catch (clearErr) {
                  console.warn('Scanner clear warning:', clearErr);
                }
              }
            } catch (err) {
              console.warn('Scanner stop warning:', err);
            }

            // Clean up container
            if (scannerContainerRef.current && scannerContainerRef.current.parentNode) {
              try {
                while (scannerContainerRef.current.firstChild) {
                  scannerContainerRef.current.removeChild(scannerContainerRef.current.firstChild);
                }
              } catch (cleanupErr) {
                console.warn('Post-scan cleanup warning:', cleanupErr);
              }
            }

            // Call success callback
            if (isActive && isMountedRef.current && onScanSuccess) {
              onScanSuccess(decodedText);
            }
          },
          (errorMessage) => {
            // Ignore frame processing errors
          }
        );

        if (isActive && isMountedRef.current) {
          setIsScanning(true);
        }
      } catch (err) {
        console.error('Error starting QR scanner:', err);
        if (isActive && isMountedRef.current) {
          if (err.message && err.message.includes('Permission')) {
            setError('Camera permission denied. Please allow camera access and try again.');
          } else {
            setError('Failed to start camera. Please check permissions and try again.');
          }
          setIsScanning(false);
        }
        html5QrCodeRef.current = null;
      }
    };

    const stopScanning = async () => {
      isActive = false;

      if (html5QrCodeRef.current) {
        const scanner = html5QrCodeRef.current;
        html5QrCodeRef.current = null;

        try {
          await scanner.stop();
        } catch (err) {
          console.warn('Stop warning:', err);
        }

        try {
          await scanner.clear();
        } catch (err) {
          console.warn('Clear warning:', err);
        }
      }

      if (isMountedRef.current) {
        setIsScanning(false);
        // Clean up the container safely
        if (scannerContainerRef.current && scannerContainerRef.current.parentNode) {
          try {
            // Use a more gentle cleanup approach
            while (scannerContainerRef.current.firstChild) {
              scannerContainerRef.current.removeChild(scannerContainerRef.current.firstChild);
            }
          } catch (err) {
            console.warn('Cleanup warning:', err);
          }
        }
      }
    };

    if (isOpen) {
      // Small delay to let modal render
      const timer = setTimeout(() => {
        if (isActive) {
          startScanning();
        }
      }, 100);

      return () => {
        clearTimeout(timer);
        stopScanning();
      };
    } else {
      stopScanning();
    }

    return () => {
      stopScanning();
    };
  }, [isOpen, onScanSuccess]);

  const handleClose = () => {
    // Scanner will be cleaned up by useEffect
    onClose();
  };

  return (
    <Modal
      open={isOpen}
      onRequestClose={handleClose}
      modalHeading="Scan QR Code"
      primaryButtonText="Close"
      onRequestSubmit={handleClose}
      size="lg"
    >
      <div style={{ padding: '24px' }}>
        {error && (
          <div style={{
            padding: '12px',
            backgroundColor: '#fff1f1',
            border: '1px solid #da1e28',
            borderRadius: '4px',
            marginBottom: '16px',
            color: '#da1e28',
          }}>
            {error}
          </div>
        )}

        <div style={{ position: 'relative' }}>
          {/* Loading message - separate from scanner container */}
          {!isScanning && !error && (
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              color: '#ffffff',
              textAlign: 'center',
              zIndex: 1,
              pointerEvents: 'none',
            }}>
              <p>Initializing camera...</p>
            </div>
          )}

          {/* Scanner container - only for html5-qrcode */}
          <div
            ref={scannerContainerRef}
            style={{
              width: '100%',
              minHeight: '400px',
              backgroundColor: '#000000',
              borderRadius: '8px',
              overflow: 'hidden',
              position: 'relative',
            }}
          />
        </div>

        <div style={{
          marginTop: '16px',
          padding: '12px',
          backgroundColor: bgSecondary,
          borderRadius: '4px',
        }}>
          <p style={{ color: textPrimary, fontSize: '0.875rem', margin: 0 }}>
            Position the QR code within the frame. Make sure the code is well-lit and in focus.
          </p>
        </div>
      </div>
    </Modal>
  );
};

export default QRCodeScanner;
